/**
 * PQL completions — registry-driven autocomplete for the statement bar
 * (LARGE-SETS-QUERY-AND-SMART-SETS-PRD.md §3.1: "field names, operators valid
 * for the field's type, live value completions").
 *
 * Pure and dependency-free beyond the tokenizer and catalog: given the
 * statement text, the caret offset, and the catalog the parser itself
 * validates against, return what may legally come next. The vocabulary is
 * the catalog and nothing else — a completion never names a field, value, or
 * operator the parser would then reject.
 *
 * The context is recovered by a small forward state machine over the tokens
 * before the caret (the grammar in §2.2), not by re-running the parser: a
 * half-typed statement is a parse error by definition, and the completions
 * have to work precisely then.
 */

import { tokenize, type Token } from './tokenizer';
import { formatValue } from './printer';
import { FIELD_ALIASES } from './catalog';
import type { FilterFieldType, PqlCatalog, PqlFieldSpec } from './types';

export type CompletionKind = 'field' | 'operator' | 'keyword' | 'value' | 'function';

export interface Completion {
  /** What the list shows. */
  label: string;
  /** Text spliced into the statement in place of the current word. */
  insert: string;
  kind: CompletionKind;
  /** Secondary text: the field's label and type, an alias target, an option label. */
  detail?: string;
}

export interface CompletionResult {
  items: Completion[];
  /** [replaceStart, replaceEnd) — the span of the statement the insert replaces. */
  replaceStart: number;
  replaceEnd: number;
  /** The word being completed (already excluded from the parse context). */
  prefix: string;
}

type State =
  | 'field' // start of a clause: a field, NOT, or (
  | 'op' // after a field: an operator, IN, NOT IN, IS [NOT] EMPTY
  | 'not-in' // after `field NOT`: only IN
  | 'is' // after IS: NOT or EMPTY
  | 'is-not' // after IS NOT: EMPTY
  | 'in-open' // after IN: (
  | 'in-value' // inside an IN list, expecting a value
  | 'in-sep' // inside an IN list, after a value: , or )
  | 'value' // after a comparison operator
  | 'range-second' // after `value ..`
  | 'after-value' // clause complete: AND, OR, ORDER BY, )
  | 'order' // after ORDER: BY
  | 'order-field' // after ORDER BY or a sort comma: a field
  | 'order-dir' // after a sort field: ASC, DESC, or ,
  | 'order-sep'; // after ASC/DESC: ,

interface Context {
  state: State;
  field: PqlFieldSpec | undefined;
  depth: number;
}

const KEYWORD_SET = new Set(['and', 'or', 'not', 'in', 'is', 'empty', 'order', 'by', 'asc', 'desc']);
const VALUE_TOKEN_TYPES = new Set(['ident', 'string', 'number', 'date', 'duration']);

const DATE_SUGGESTIONS: Completion[] = [
  { label: 'now()', insert: 'now()', kind: 'function', detail: 'today' },
  { label: '-7d', insert: '-7d', kind: 'value', detail: '7 days ago' },
  { label: '-30d', insert: '-30d', kind: 'value', detail: '30 days ago' },
  { label: '-90d', insert: '-90d', kind: 'value', detail: '90 days ago' },
  { label: '-12w', insert: '-12w', kind: 'value', detail: '12 weeks ago' },
  { label: '-6M', insert: '-6M', kind: 'value', detail: '6 months ago' },
];

function isKeyword(t: Token | undefined, kw: string): boolean {
  return !!t && t.type === 'ident' && t.text.toLowerCase() === kw;
}

/** Split the statement at the caret into (settled tokens, in-progress word). */
function splitAtCaret(text: string, cursor: number): { tokens: Token[]; prefix: string; replaceStart: number } {
  const head = text.slice(0, cursor);
  let tokens: Token[];
  try {
    tokens = tokenize(head);
  } catch {
    // An unclosed quote: everything from the quote on is the word in progress.
    const q = Math.max(head.lastIndexOf('"'), head.lastIndexOf("'"));
    if (q < 0) return { tokens: [], prefix: head, replaceStart: 0 };
    let before: Token[] = [];
    try {
      before = tokenize(head.slice(0, q));
    } catch {
      before = [];
    }
    return { tokens: before.filter((t) => t.type !== 'eof'), prefix: head.slice(q + 1), replaceStart: q };
  }
  tokens = tokens.filter((t) => t.type !== 'eof');
  const last = tokens[tokens.length - 1];
  const touching = last && last.end === cursor && (last.type === 'ident' || last.type === 'number' || last.type === 'duration' || last.type === 'date');
  if (touching) {
    return { tokens: tokens.slice(0, -1), prefix: last.text, replaceStart: last.start };
  }
  return { tokens, prefix: '', replaceStart: cursor };
}

/** Walk the settled tokens through the grammar to find what comes next. */
function deriveContext(tokens: Token[], catalog: PqlCatalog): Context {
  const ctx: Context = { state: 'field', field: undefined, depth: 0 };
  for (let i = 0; i < tokens.length; i++) {
    const t = tokens[i];
    const next = tokens[i + 1];
    switch (ctx.state) {
      case 'field':
        if (t.type === 'lparen') { ctx.depth++; break; }
        if (isKeyword(t, 'not')) break;
        if (t.type === 'ident' && !KEYWORD_SET.has(t.text.toLowerCase())) {
          ctx.field = catalog.resolveField(t.text);
          ctx.state = 'op';
        }
        break;
      case 'op':
        if (t.type === 'op') { ctx.state = 'value'; break; }
        if (isKeyword(t, 'in')) { ctx.state = 'in-open'; break; }
        if (isKeyword(t, 'not')) { ctx.state = 'not-in'; break; }
        if (isKeyword(t, 'is')) { ctx.state = 'is'; break; }
        break;
      case 'not-in':
        if (isKeyword(t, 'in')) ctx.state = 'in-open';
        break;
      case 'is':
        if (isKeyword(t, 'not')) { ctx.state = 'is-not'; break; }
        if (isKeyword(t, 'empty')) ctx.state = 'after-value';
        break;
      case 'is-not':
        if (isKeyword(t, 'empty')) ctx.state = 'after-value';
        break;
      case 'in-open':
        if (t.type === 'lparen') ctx.state = 'in-value';
        break;
      case 'in-value':
        if (VALUE_TOKEN_TYPES.has(t.type)) {
          if (t.type === 'ident' && next?.type === 'lparen') { i += 2; } // fn()
          ctx.state = 'in-sep';
        }
        break;
      case 'in-sep':
        if (t.type === 'comma') { ctx.state = 'in-value'; break; }
        if (t.type === 'rparen') ctx.state = 'after-value';
        break;
      case 'value':
        if (VALUE_TOKEN_TYPES.has(t.type)) {
          if (t.type === 'ident' && next?.type === 'lparen') { i += 2; }
          ctx.state = 'after-value';
        }
        break;
      case 'range-second':
        if (VALUE_TOKEN_TYPES.has(t.type)) ctx.state = 'after-value';
        break;
      case 'after-value':
        if (t.type === 'dotdot') { ctx.state = 'range-second'; break; }
        if (t.type === 'rparen') { ctx.depth = Math.max(0, ctx.depth - 1); break; }
        if (isKeyword(t, 'and') || isKeyword(t, 'or')) { ctx.state = 'field'; ctx.field = undefined; break; }
        if (isKeyword(t, 'order')) ctx.state = 'order';
        break;
      case 'order':
        if (isKeyword(t, 'by')) ctx.state = 'order-field';
        break;
      case 'order-field':
        if (t.type === 'ident' && !KEYWORD_SET.has(t.text.toLowerCase())) ctx.state = 'order-dir';
        break;
      case 'order-dir':
        if (isKeyword(t, 'asc') || isKeyword(t, 'desc')) { ctx.state = 'order-sep'; break; }
        if (t.type === 'comma') ctx.state = 'order-field';
        break;
      case 'order-sep':
        if (t.type === 'comma') ctx.state = 'order-field';
        break;
    }
  }
  return ctx;
}

function formatFieldName(key: string): string {
  return key.startsWith('meta:') ? `meta.${key.slice(5)}` : key;
}

const TYPE_LABEL: Record<FilterFieldType, string> = {
  set: 'choice',
  boolean: 'yes/no',
  text: 'text',
  'date-range': 'date',
  range: 'number',
};

function fieldCompletions(catalog: PqlCatalog): Completion[] {
  const items: Completion[] = catalog.fields.map((f) => ({
    label: formatFieldName(f.key),
    insert: formatFieldName(f.key),
    kind: 'field' as const,
    detail: `${f.label} · ${TYPE_LABEL[f.type]}`,
  }));
  const present = new Set(catalog.fields.map((f) => f.key));
  for (const [alias, target] of Object.entries(FIELD_ALIASES)) {
    if (present.has(target) && !present.has(alias)) {
      items.push({ label: alias, insert: alias, kind: 'field', detail: `→ ${formatFieldName(target)}` });
    }
  }
  return items;
}

function operatorCompletions(field: PqlFieldSpec | undefined): Completion[] {
  const op = (label: string, detail: string): Completion => ({ label, insert: label, kind: 'operator', detail });
  if (!field) {
    return [op('=', 'equals'), op('!=', 'does not equal'), op('IN', 'is one of'), op('~', 'contains')];
  }
  switch (field.type) {
    case 'boolean':
      return [op('=', 'is'), op('!=', 'is not')];
    case 'text':
      return [op('~', 'contains'), op('!~', 'does not contain'), op('=', 'equals'), op('!=', 'does not equal'),
        op('IS EMPTY', 'has no value'), op('IS NOT EMPTY', 'has a value')];
    case 'range':
    case 'date-range':
      return [op('=', 'equals'), op('!=', 'does not equal'), op('>', 'after / greater than'), op('>=', 'at least'),
        op('<', 'before / less than'), op('<=', 'at most'), op('IS EMPTY', 'has no value'), op('IS NOT EMPTY', 'has a value')];
    case 'set': {
      const items = [op('=', 'is'), op('!=', 'is not'), op('IN', 'is one of'), op('NOT IN', 'is none of'),
        op('~', 'contains'), op('!~', 'does not contain')];
      const options = field.options ?? [];
      if (options.length > 0 && options.every((o) => !Number.isNaN(Number(o.value)))) {
        items.push(op('>', 'greater than'), op('>=', 'at least'), op('<', 'less than'), op('<=', 'at most'));
      }
      items.push(op('IS EMPTY', 'has no value'), op('IS NOT EMPTY', 'has a value'));
      return items;
    }
  }
}

function valueCompletions(field: PqlFieldSpec | undefined, catalog: PqlCatalog): Completion[] {
  if (!field) return [];
  switch (field.type) {
    case 'boolean':
      return [
        { label: 'true', insert: 'true', kind: 'value', detail: 'yes' },
        { label: 'false', insert: 'false', kind: 'value', detail: 'no' },
      ];
    case 'date-range':
      return DATE_SUGGESTIONS;
    case 'set': {
      const items: Completion[] = (field.options ?? []).map((o) => ({
        label: o.label === o.value ? o.value : `${o.label}`,
        insert: formatValue(o.value),
        kind: 'value' as const,
        detail: o.label === o.value ? undefined : o.value,
      }));
      if (catalog.context.currentUserId && (field.options ?? []).length === 0) {
        items.unshift({ label: 'currentUser()', insert: 'currentUser()', kind: 'function', detail: 'you' });
      }
      return items;
    }
    default:
      return [];
  }
}

function keywordCompletions(labels: string[]): Completion[] {
  return labels.map((k) => ({ label: k, insert: k, kind: 'keyword' as const }));
}

function candidatesFor(ctx: Context, catalog: PqlCatalog): Completion[] {
  switch (ctx.state) {
    case 'field':
      return [...fieldCompletions(catalog), ...keywordCompletions(['NOT'])];
    case 'op':
      return operatorCompletions(ctx.field);
    case 'not-in':
      return keywordCompletions(['IN']);
    case 'is':
      return keywordCompletions(['EMPTY', 'NOT EMPTY']);
    case 'is-not':
      return keywordCompletions(['EMPTY']);
    case 'in-open':
      return [{ label: '(', insert: '(', kind: 'keyword' }];
    case 'in-value':
    case 'value':
    case 'range-second':
      return valueCompletions(ctx.field, catalog);
    case 'in-sep':
      return [];
    case 'after-value':
      return keywordCompletions(ctx.depth > 0 ? ['AND', 'OR', ')'] : ['AND', 'OR', 'ORDER BY']);
    case 'order':
      return keywordCompletions(['BY']);
    case 'order-field':
      return fieldCompletions(catalog);
    case 'order-dir':
      return keywordCompletions(['ASC', 'DESC']);
    case 'order-sep':
      return [];
  }
}

function matchesPrefix(item: Completion, prefix: string): boolean {
  if (!prefix) return true;
  const p = prefix.toLowerCase();
  if (item.label.toLowerCase().startsWith(p) || item.insert.toLowerCase().startsWith(p)) return true;
  // A quoted value in progress: "ent → "enterprise"
  if (item.insert.startsWith('"') && item.insert.slice(1).toLowerCase().startsWith(p)) return true;
  // Fields also match on their human label ("stat" → exit_status via "Status").
  if (item.kind === 'field' && item.detail && item.detail.toLowerCase().startsWith(p)) return true;
  return false;
}

/**
 * Completions at `cursor` in `text`. Never throws: an unparseable prefix
 * yields whatever the last recoverable context allows (or nothing).
 */
export function completeAt(text: string, cursor: number, catalog: PqlCatalog): CompletionResult {
  const at = Math.max(0, Math.min(cursor, text.length));
  const { tokens, prefix, replaceStart } = splitAtCaret(text, at);
  const ctx = deriveContext(tokens, catalog);
  let items = candidatesFor(ctx, catalog).filter((c) => matchesPrefix(c, prefix));
  // Exact match on a single field/keyword means the word is done — nothing to offer.
  if (items.length === 1 && items[0].insert.toLowerCase() === prefix.toLowerCase() && items[0].kind !== 'value') {
    items = [];
  }
  return { items, replaceStart, replaceEnd: at, prefix };
}

/** Splice a completion into the statement; returns the new text and caret. */
export function applyCompletion(
  text: string,
  result: CompletionResult,
  item: Completion,
): { text: string; cursor: number } {
  const before = text.slice(0, result.replaceStart);
  const after = text.slice(result.replaceEnd);
  const trailing = item.insert.endsWith('(') || after.startsWith(' ') ? '' : ' ';
  const inserted = `${before}${item.insert}${trailing}`;
  return { text: `${inserted}${after}`, cursor: inserted.length };
}
